/**
 * ListViewTable Component
 * 
 * A reusable, metadata-driven table component that renders list views
 * similar to Salesforce list views. All columns, sorting, and pagination
 * are driven by metadata from the backend.
 * 
 * Features:
 * - Dynamic column rendering from metadata
 * - Column ordering and width support
 * - Sorting (click column headers)
 * - Pagination
 * - Clean, dense, data-first design
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Pressable,
  Platform,
  Dimensions,
} from 'react-native';
import { useTheme } from '../context/ThemeContext';
import { useUserPreferences } from '../context/UserPreferencesContext';
import { formatDateForLocaleDisplay } from '../utils/dateFormat';
import { getApiUrl } from '../utils/apiConfig';
import RecordCellEditor from './RecordCellEditor';

const { width: SCREEN_WIDTH } = Dimensions.get('window');
const SELECT_COLUMN_WIDTH = 44;
const SEARCH_FETCH_CAP = 2000;

function recordMatchesSearch(record, columns, query, formatCellValue) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;

  for (const column of columns) {
    const fieldName = column.fieldName || column.field_name;
    if (!fieldName) continue;
    const display = formatCellValue(record[fieldName], fieldName).toLowerCase();
    if (display.includes(q)) return true;
  }
  return false;
}

function RecordCheckbox({ checked, onPress, theme, gold }) {
  return (
    <TouchableOpacity
      onPress={onPress}
      style={stylesCheckbox.hit}
      accessibilityRole="checkbox"
      accessibilityState={{ checked }}
      hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
    >
      <View
        style={[
          stylesCheckbox.box,
          checked && stylesCheckbox.boxChecked,
          {
            borderColor: gold,
            backgroundColor: checked
              ? gold
              : theme.isDarkMode
                ? '#1a1a1a'
                : '#FFFFFF',
          },
        ]}
      >
        {checked ? <Text style={stylesCheckbox.checkmark}>✓</Text> : null}
      </View>
    </TouchableOpacity>
  );
}

const stylesCheckbox = StyleSheet.create({
  hit: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 4,
  },
  box: {
    width: 22,
    height: 22,
    borderRadius: 4,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  boxChecked: {
    borderColor: 'transparent',
  },
  checkmark: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: 'bold',
    lineHeight: 18,
    marginTop: Platform.OS === 'ios' ? -1 : 0,
  },
});

export default function ListViewTable({
  listViewId,
  onRecordPress,
  onSortChange,
  initialPage = 1,
  recordsPerPage = 20,
  refreshKey = 0,
  enableBulkSelect = false,
  selectedRecordIds = [],
  onSelectedRecordIdsChange,
  editMode = false,
  editableRecordIds = [],
  fieldMetaByName = {},
  draftEdits = {},
  onDraftValueChange,
  onPageRecordsChange,
  searchQuery = '',
}) {
  const theme = useTheme();
  const { isUSUser } = useUserPreferences();
  const [records, setRecords] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [totalRecords, setTotalRecords] = useState(0);
  const totalRecordsRef = useRef(0);
  const [totalPages, setTotalPages] = useState(0);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('DESC');
  const [listViewInfo, setListViewInfo] = useState(null);
  const [columnWidths, setColumnWidths] = useState({});
  const [resizingColumn, setResizingColumn] = useState(null);
  const [resizeStartX, setResizeStartX] = useState(0);
  const [resizeStartWidth, setResizeStartWidth] = useState(0);
  const horizontalScrollRef = useRef(null);
  const [hScrollX, setHScrollX] = useState(0);
  const [hViewportW, setHViewportW] = useState(0);
  const [hContentW, setHContentW] = useState(0);
  const [hTrackW, setHTrackW] = useState(0);

  const trimmedSearchQuery = String(searchQuery || '').trim();
  const isSearchActive = trimmedSearchQuery.length > 0;

  useEffect(() => {
    totalRecordsRef.current = 0;
    setTotalRecords(0);
  }, [listViewId]);

  useEffect(() => {
    fetchRecords();
  }, [listViewId, currentPage, sortField, sortDirection, refreshKey, isSearchActive]);

  useEffect(() => {
    if (String(searchQuery || '').trim()) {
      setCurrentPage(1);
    }
  }, [isSearchActive]);

  const formatCellValue = (value, fieldName) => {
    if (value === null || value === undefined) {
      return '';
    }

    if (value instanceof Date || (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value))) {
      return formatDateForLocaleDisplay(value, isUSUser);
    }

    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    return String(value);
  };

  const fetchRecords = async () => {
    if (!listViewId) return;

    const trimmedSearch = String(searchQuery || '').trim();
    const isSearchMode = trimmedSearch.length > 0;
    const fetchPage = isSearchMode ? 1 : currentPage;
    const knownTotal = totalRecordsRef.current || 0;
    const fetchLimit = isSearchMode
      ? Math.min(Math.max(knownTotal, recordsPerPage), SEARCH_FETCH_CAP)
      : recordsPerPage;

    setLoading(true);
    try {
      const response = await fetch(
        `${getApiUrl()}/api/list-views/${listViewId}/records?page=${fetchPage}&limit=${fetchLimit}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setRecords(data.records || []);
      setColumns(data.columns || []);
      const total = data.total || 0;
      setTotalRecords(total);
      totalRecordsRef.current = total;
      setTotalPages(isSearchMode ? 1 : data.totalPages || 0);
      setListViewInfo(data.listView || null);
      
      // Initialize column widths from metadata or defaults
      const initialWidths = {};
      (data.columns || []).forEach((col) => {
        const fieldName = col.fieldName || col.field_name;
        initialWidths[fieldName] = col.width || 150;
      });
      setColumnWidths(prev => ({ ...prev, ...initialWidths }));
    } catch (error) {
      console.error('Error fetching list view records:', error);
      setRecords([]);
      setColumns([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (fieldName) => {
    if (sortField === fieldName) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortField(fieldName);
      setSortDirection('ASC');
    }
    setCurrentPage(1); // Reset to first page on sort
    if (onSortChange) {
      onSortChange(fieldName, sortField === fieldName && sortDirection === 'ASC' ? 'DESC' : 'ASC');
    }
  };

  const maxHScroll =
    hViewportW > 0 ? Math.max(0, hContentW - hViewportW) : 0;

  const scrollHorizontalBy = (delta) => {
    if (maxHScroll <= 0) return;
    const step = Math.max(80, Math.round(hViewportW * 0.35));
    const next = Math.max(0, Math.min(maxHScroll, hScrollX + (delta > 0 ? step : -step)));
    horizontalScrollRef.current?.scrollTo?.({ x: next, animated: true });
  };

  const handleHorizontalTrackPress = (e) => {
    const x = e.nativeEvent.locationX;
    if (maxHScroll <= 0 || hTrackW <= 0) return;
    const ratio = Math.max(0, Math.min(1, x / hTrackW));
    horizontalScrollRef.current?.scrollTo?.({ x: ratio * maxHScroll, animated: true });
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Column resizing handlers
  const handleResizeStart = (fieldName, startX) => {
    const fieldNameKey = fieldName;
    const currentWidth = columnWidths[fieldNameKey] || 150;
    setResizingColumn(fieldNameKey);
    setResizeStartX(startX);
    setResizeStartWidth(currentWidth);
  };

  const handleResizeMove = (currentX) => {
    if (resizingColumn) {
      const deltaX = currentX - resizeStartX;
      const newWidth = Math.max(50, resizeStartWidth + deltaX); // Minimum width 50px
      setColumnWidths({
        ...columnWidths,
        [resizingColumn]: newWidth,
      });
    }
  };

  const handleResizeEnd = () => {
    setResizingColumn(null);
    setResizeStartX(0);
    setResizeStartWidth(0);
  };

  // Global mouse handlers for web resizing
  useEffect(() => {
    if (Platform.OS === 'web' && resizingColumn) {
      const handleMouseMove = (e) => {
        e.preventDefault();
        const deltaX = e.clientX - resizeStartX;
        const newWidth = Math.max(50, resizeStartWidth + deltaX);
        setColumnWidths(prev => ({
          ...prev,
          [resizingColumn]: newWidth,
        }));
      };

      const handleMouseUp = () => {
        handleResizeEnd();
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }
  }, [resizingColumn, resizeStartX, resizeStartWidth]);

  const displayRecords = useMemo(() => {
    if (!isSearchActive) return records;
    return records.filter((record) =>
      recordMatchesSearch(record, columns, trimmedSearchQuery, formatCellValue)
    );
  }, [records, columns, trimmedSearchQuery, isSearchActive, isUSUser]);

  useEffect(() => {
    onPageRecordsChange?.(displayRecords);
  }, [displayRecords, onPageRecordsChange]);

  const styles = getStyles(theme);
  const gold = theme.colors.primary || theme.colors.heading || '#d4af37';

  const selectedSet = useMemo(
    () => new Set((selectedRecordIds || []).map((id) => String(id))),
    [selectedRecordIds]
  );

  const editableSet = useMemo(
    () => new Set((editableRecordIds || []).map((id) => String(id))),
    [editableRecordIds]
  );

  const isFieldEditable = (fieldName) => {
    const meta = fieldMetaByName[fieldName];
    if (meta && meta.is_editable === false) return false;
    if (meta && meta.is_editable === true) return true;
    return false;
  };

  const getDraftValue = (recordId, fieldName, record) => {
    const rid = String(recordId);
    if (draftEdits[rid] && Object.prototype.hasOwnProperty.call(draftEdits[rid], fieldName)) {
      return draftEdits[rid][fieldName];
    }
    return record[fieldName];
  };

  const pageRecordIds = useMemo(
    () => displayRecords.map((r) => String(r.id)).filter((id) => id !== 'undefined' && id !== 'null'),
    [displayRecords]
  );

  const allPageSelected =
    enableBulkSelect &&
    pageRecordIds.length > 0 &&
    pageRecordIds.every((id) => selectedSet.has(id));

  const toggleRecordSelection = (recordId) => {
    if (!onSelectedRecordIdsChange) return;
    const sid = String(recordId);
    const next = new Set(selectedSet);
    if (next.has(sid)) next.delete(sid);
    else next.add(sid);
    onSelectedRecordIdsChange([...next]);
  };

  const toggleSelectAllOnPage = () => {
    if (!onSelectedRecordIdsChange) return;
    const next = new Set(selectedSet);
    if (allPageSelected) {
      pageRecordIds.forEach((id) => next.delete(id));
    } else {
      pageRecordIds.forEach((id) => next.add(id));
    }
    onSelectedRecordIdsChange([...next]);
  };

  if (loading) {
    return (
      <View style={styles.container}>
        <Text style={styles.loadingText}>Loading...</Text>
      </View>
    );
  }

  if (columns.length === 0) {
    return (
      <View style={styles.container}>
        <Text style={styles.emptyText}>No columns defined for this list view</Text>
      </View>
    );
  }

  if (records.length === 0) {
    return (
      <View style={styles.container}>
        <Text style={styles.emptyText}>No records found</Text>
      </View>
    );
  }

  if (displayRecords.length === 0) {
    return (
      <View style={styles.container}>
        <Text style={styles.emptyText}>No records match your search</Text>
      </View>
    );
  }

  const thumbW =
    maxHScroll > 0 && hTrackW > 0 && hContentW > 0
      ? Math.max(40, Math.min(hTrackW, (hViewportW / hContentW) * hTrackW))
      : 48;
  const thumbLeft =
    maxHScroll > 0 && hTrackW > 0 && thumbW > 0
      ? (hScrollX / maxHScroll) * Math.max(0, hTrackW - thumbW)
      : 0;

  return (
    <View style={styles.container}>
      {/* Vertical ScrollView for table rows */}
      <ScrollView 
        style={styles.verticalScrollView}
        contentContainerStyle={styles.verticalScrollContent}
        showsVerticalScrollIndicator={true}
      >
        {/* Horizontal ScrollView for table columns */}
        <ScrollView
          ref={horizontalScrollRef}
          horizontal
          showsHorizontalScrollIndicator={true}
          style={styles.tableScrollView}
          contentContainerStyle={styles.tableScrollContent}
          scrollEventThrottle={16}
          onScroll={(ev) => setHScrollX(ev.nativeEvent.contentOffset.x)}
          onContentSizeChange={(w) => setHContentW(w)}
          onLayout={(ev) => setHViewportW(ev.nativeEvent.layout.width)}
          {...(Platform.OS === 'web'
            ? {
                // @ts-ignore — RN web
                className: 'records-list-horizontal-scroll',
              }
            : {})}
        >
          <View style={styles.tableContainer}>
            <View style={styles.tableHeader}>
              {enableBulkSelect ? (
                <View style={[styles.selectHeaderCell, { width: SELECT_COLUMN_WIDTH }]}>
                  <RecordCheckbox
                    checked={allPageSelected}
                    onPress={toggleSelectAllOnPage}
                    theme={theme}
                    gold={gold}
                  />
                </View>
              ) : null}
              {columns.map((column, index) => {
                const fieldName = column.fieldName || column.field_name;
                const isSorted = sortField === fieldName;
                const columnWidth = columnWidths[fieldName] || column.width || 150;
                const isLastColumn = index === columns.length - 1;
                
                return (
                  <View
                    key={fieldName || index}
                    style={[
                      styles.headerCellContainer,
                      { width: columnWidth },
                    ]}
                  >
                    <TouchableOpacity
                      style={styles.headerCell}
                      onPress={() => handleSort(fieldName)}
                    >
                      <Text style={styles.headerText} numberOfLines={1}>
                        {column.label}
                      </Text>
                      {isSorted && (
                        <Text style={styles.sortIndicator}>
                          {sortDirection === 'ASC' ? '↑' : '↓'}
                        </Text>
                      )}
                    </TouchableOpacity>
                    {!isLastColumn && (
                      <View
                        style={[
                          styles.resizeHandle,
                          resizingColumn === fieldName && styles.resizeHandleActive,
                        ]}
                        onTouchStart={(e) => {
                          const touch = e.nativeEvent.touches[0];
                          handleResizeStart(fieldName, touch.pageX);
                        }}
                        onTouchMove={(e) => {
                          if (resizingColumn === fieldName) {
                            const touch = e.nativeEvent.touches[0];
                            handleResizeMove(touch.pageX);
                          }
                        }}
                        onTouchEnd={() => {
                          if (resizingColumn === fieldName) {
                            handleResizeEnd();
                          }
                        }}
                        {...(Platform.OS === 'web' && {
                          onMouseDown: (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleResizeStart(fieldName, e.clientX);
                          },
                        })}
                      />
                    )}
                  </View>
                );
              })}
            </View>

            {/* Table Rows */}
            {displayRecords.map((record, rowIndex) => {
              const rowId = record.id != null ? String(record.id) : String(rowIndex);
              const isRowSelected = enableBulkSelect && selectedSet.has(rowId);
              return (
              <View
                key={rowId}
                style={[
                  styles.tableRow,
                  rowIndex % 2 === 0 && styles.tableRowEven,
                  isRowSelected && styles.tableRowSelected,
                ]}
              >
                {enableBulkSelect ? (
                  <View style={[styles.selectCell, { width: SELECT_COLUMN_WIDTH }]}>
                    <RecordCheckbox
                      checked={isRowSelected}
                      onPress={() => toggleRecordSelection(record.id)}
                      theme={theme}
                      gold={gold}
                    />
                  </View>
                ) : null}
                {columns.map((column, colIndex) => {
                  const fieldName = column.fieldName || column.field_name;
                  const columnWidth = columnWidths[fieldName] || column.width || 150;
                  const rowIsEditable =
                    editMode && editableSet.has(rowId) && isFieldEditable(fieldName);
                  const cellValue = getDraftValue(record.id, fieldName, record);
                  const isIdColumn = fieldName === 'id';
                  return (
                    <View
                      key={fieldName || colIndex}
                      style={[
                        styles.tableCell,
                        { width: columnWidth },
                        rowIsEditable && styles.tableCellEditing,
                      ]}
                    >
                      {rowIsEditable ? (
                        <RecordCellEditor
                          value={cellValue}
                          fieldMeta={fieldMetaByName[fieldName]}
                          theme={theme}
                          isUSUser={isUSUser}
                          onChange={(next) =>
                            onDraftValueChange?.(record.id, fieldName, next)
                          }
                        />
                      ) : isIdColumn && onRecordPress ? (
                        <TouchableOpacity
                          style={styles.tableCellPressable}
                          activeOpacity={0.65}
                          onPress={() => onRecordPress(record)}
                        >
                          <Text
                            style={[styles.cellText, styles.idLinkText]}
                            numberOfLines={1}
                            ellipsizeMode="tail"
                          >
                            {formatCellValue(record[fieldName], fieldName)}
                          </Text>
                        </TouchableOpacity>
                      ) : (
                        <View style={styles.tableCellPressable}>
                          <Text
                            style={styles.cellText}
                            numberOfLines={1}
                            ellipsizeMode="tail"
                          >
                            {formatCellValue(record[fieldName], fieldName)}
                          </Text>
                        </View>
                      )}
                    </View>
                  );
                })}
              </View>
            );
            })}
          </View>
        </ScrollView>
      </ScrollView>

      {maxHScroll > 0 && (
        <View style={styles.hScrollBar}>
          <TouchableOpacity
            style={styles.hScrollArrow}
            onPress={() => scrollHorizontalBy(-1)}
            accessibilityLabel="Scroll table left"
          >
            <Text style={styles.hScrollArrowText}>◀</Text>
          </TouchableOpacity>
          <Pressable
            style={styles.hScrollTrack}
            onLayout={(ev) => setHTrackW(ev.nativeEvent.layout.width)}
            onPress={handleHorizontalTrackPress}
          >
            <View
              pointerEvents="none"
              style={[
                styles.hScrollThumb,
                {
                  width: thumbW,
                  left: thumbLeft,
                },
              ]}
            />
          </Pressable>
          <TouchableOpacity
            style={styles.hScrollArrow}
            onPress={() => scrollHorizontalBy(1)}
            accessibilityLabel="Scroll table right"
          >
            <Text style={styles.hScrollArrowText}>▶</Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Pagination */}
      {totalRecords > 0 && !isSearchActive && (
        <View style={styles.pagination}>
          <TouchableOpacity
            style={[styles.pageButton, currentPage === 1 && styles.pageButtonDisabled]}
            onPress={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1 || totalPages <= 1}
          >
            <Text style={styles.pageButtonText}>Previous</Text>
          </TouchableOpacity>

          <View
            style={styles.pageInfoBlock}
            {...(Platform.OS === 'web' ? { dataSet: { listViewPaginationInfo: 'true' } } : {})}
          >
            <Text style={styles.pageInfoLine}>
              Page {currentPage} of {Math.max(totalPages, 1)}
            </Text>
            <Text style={styles.pageInfoMeta}>{totalRecords} records total</Text>
          </View>

          <TouchableOpacity
            style={[
              styles.pageButton,
              currentPage === totalPages && styles.pageButtonDisabled,
            ]}
            onPress={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages || totalPages <= 1}
          >
            <Text style={styles.pageButtonText}>Next</Text>
          </TouchableOpacity>
        </View>
      )}

      {isSearchActive && (
        <View style={styles.pagination}>
          <View
            style={styles.pageInfoBlock}
            {...(Platform.OS === 'web' ? { dataSet: { listViewPaginationInfo: 'true' } } : {})}
          >
            <Text style={styles.pageInfoLine}>
              {displayRecords.length} match{displayRecords.length === 1 ? '' : 'es'}
            </Text>
            {totalRecords > displayRecords.length ? (
              <Text style={styles.pageInfoMeta}>
                Searched {records.length} of {totalRecords} records
              </Text>
            ) : null}
          </View>
        </View>
      )}
    </View>
  );
}

function getStyles(theme) {
  const gold = theme.colors.primary || '#d4af37';
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.isDarkMode ? '#000000' : '#FFFFFF',
      alignItems: 'stretch',
      width: '100%',
    },
    verticalScrollView: {
      flex: 1,
      width: '100%',
      ...(Platform.OS === 'web' && {
        maxHeight: '100%',
        overflowY: 'auto',
      }),
    },
    verticalScrollContent: {
      alignItems: 'stretch',
      paddingBottom: 20,
      ...(Platform.OS === 'web' && {
        minHeight: '100%',
      }),
    },
    tableScrollView: {
      width: '100%',
      alignSelf: 'stretch',
      ...(Platform.OS === 'web' && {
        overflowX: 'auto',
      }),
    },
    tableScrollContent: {
      alignItems: 'flex-start',
    },
    tableContainer: {
      width: '100%',
      minWidth: SCREEN_WIDTH * 0.9,
    },
    tableHeader: {
      flexDirection: 'row',
      backgroundColor: theme.isDarkMode ? '#1a1a1a' : '#F5F5F5',
      borderBottomWidth: 2,
      borderBottomColor: theme.isDarkMode ? '#333333' : '#CCCCCC',
      ...(Platform.OS === 'web' && {
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }),
    },
    headerCellContainer: {
      position: 'relative',
      minWidth: 50,
    },
    headerCell: {
      padding: 12,
      flex: 1,
      borderRightWidth: 1,
      borderRightColor: theme.isDarkMode ? '#333333' : '#E0E0E0',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      minHeight: 44,
    },
    resizeHandle: {
      position: 'absolute',
      right: -4,
      top: 0,
      bottom: 0,
      width: 8,
      zIndex: 20,
      backgroundColor: 'transparent',
      ...(Platform.OS === 'web' && {
        cursor: 'col-resize',
      }),
    },
    resizeHandleActive: {
      backgroundColor: theme.isDarkMode ? '#FDFD0A' : gold,
      opacity: 0.5,
    },
    headerText: {
      fontSize: 12,
      fontWeight: '600',
      color: theme.isDarkMode ? '#FDFD0A' : '#000000',
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    sortIndicator: {
      fontSize: 10,
      color: theme.isDarkMode ? '#FDFD0A' : '#000000',
      marginLeft: 4,
    },
    tableRow: {
      flexDirection: 'row',
      borderBottomWidth: 1,
      borderBottomColor: theme.isDarkMode ? '#1a1a1a' : '#F0F0F0',
    },
    tableRowEven: {
      backgroundColor: theme.isDarkMode ? '#0a0a0a' : '#FAFAFA',
    },
    tableRowSelected: {
      backgroundColor: theme.isDarkMode ? 'rgba(212, 175, 55, 0.22)' : 'rgba(212, 175, 55, 0.18)',
    },
    selectHeaderCell: {
      justifyContent: 'center',
      alignItems: 'center',
      borderRightWidth: 1,
      borderRightColor: theme.isDarkMode ? '#333333' : '#E0E0E0',
      minHeight: 44,
    },
    selectCell: {
      justifyContent: 'center',
      alignItems: 'center',
      borderRightWidth: 1,
      borderRightColor: theme.isDarkMode ? '#1a1a1a' : '#F0F0F0',
    },
    tableCell: {
      padding: 12,
      minWidth: 100,
      borderRightWidth: 1,
      borderRightColor: theme.isDarkMode ? '#1a1a1a' : '#F0F0F0',
      justifyContent: 'center',
      backgroundColor: 'transparent',
    },
    tableCellEditing: {
      zIndex: 20,
      overflow: 'visible',
    },
    tableCellPressable: {
      flex: 1,
      justifyContent: 'center',
      backgroundColor: 'transparent',
    },
    cellText: {
      fontSize: 14,
      color: theme.isDarkMode ? '#FFFFFF' : '#000000',
      backgroundColor: 'transparent',
    },
    idLinkText: {
      color: theme.isDarkMode ? '#d4af37' : '#0176d3',
      fontWeight: '600',
      textDecorationLine: 'underline',
      backgroundColor: 'transparent',
    },
    loadingText: {
      fontSize: 16,
      color: theme.isDarkMode ? '#FFFFFF' : '#000000',
      textAlign: 'center',
      padding: 20,
    },
    emptyText: {
      fontSize: 16,
      color: theme.isDarkMode ? '#888888' : '#666666',
      textAlign: 'center',
      padding: 20,
    },
    pagination: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: 15,
      borderTopWidth: 1,
      borderTopColor: theme.isDarkMode ? '#333333' : '#E0E0E0',
      backgroundColor: theme.isDarkMode ? 'transparent' : '#FAFAFA',
    },
    pageButton: {
      paddingHorizontal: 16,
      paddingVertical: 8,
      backgroundColor: theme.isDarkMode ? '#FDFD0A' : gold,
      borderRadius: 4,
      flexShrink: 0,
    },
    pageButtonDisabled: {
      backgroundColor: theme.isDarkMode ? '#333333' : '#CCCCCC',
      opacity: 0.5,
    },
    pageButtonText: {
      color: '#000000',
      fontSize: 14,
      fontWeight: '600',
    },
    pageInfoBlock: {
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: 8,
      flex: 1,
      backgroundColor: 'transparent',
    },
    pageInfoLine: {
      fontSize: 12,
      color: theme.isDarkMode ? '#FFFFFF' : '#000000',
      fontWeight: '500',
      backgroundColor: 'transparent',
    },
    pageInfoMeta: {
      fontSize: 11,
      marginTop: 2,
      color: theme.isDarkMode ? '#CCCCCC' : '#555555',
      backgroundColor: 'transparent',
    },
    hScrollBar: {
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'nowrap',
      alignSelf: 'stretch',
      paddingVertical: 10,
      paddingHorizontal: 12,
      marginTop: 0,
      borderTopWidth: 1,
      borderTopColor: theme.isDarkMode ? '#333333' : '#E0E0E0',
      borderRadius: 10,
      backgroundColor: theme.isDarkMode ? '#141414' : '#EEEEEE',
      width: '100%',
      gap: 10,
    },
    hScrollArrow: {
      width: 36,
      height: 36,
      borderRadius: 8,
      justifyContent: 'center',
      alignItems: 'center',
      flexShrink: 0,
      backgroundColor: theme.isDarkMode ? '#262626' : '#E8E8E8',
    },
    hScrollArrowText: {
      fontSize: 13,
      lineHeight: 15,
      color: theme.isDarkMode ? '#FFFFFF' : '#000000',
      fontWeight: '700',
    },
    hScrollTrack: {
      flex: 1,
      flexShrink: 1,
      minWidth: 56,
      height: 12,
      borderRadius: 6,
      backgroundColor: theme.isDarkMode ? '#333333' : '#D0D0D0',
      position: 'relative',
      overflow: 'hidden',
    },
    hScrollThumb: {
      position: 'absolute',
      top: 0,
      height: 12,
      borderRadius: 6,
      backgroundColor: theme.isDarkMode ? '#FFFF00' : gold,
    },
  });
}
